import React, { useState, useEffect } from 'react';
import {
  Typography, Alert,
  useTheme, useMediaQuery,
} from '@mui/material';
import ModalWrapper from '../../components/ModalWrapper';
import StepWizard from '../../components/StepWizard';
import { apiFetch } from '../../apiFetch';
import {
  Grocery, GroceryFormData, emptyGroceryFormData,
  GrocerySearchResult, GroceryCatalogItem,
} from '../../types/Grocery';
import { ShoppingListGrocery } from '../../types/ShoppingListGrocery';
import { Price } from '../../types/Price';
import { AddGroceryResult } from './types';
import FindGroceryStep from './FindGroceryStep';
import AddToListStep from './AddToListStep';
import EnterPriceStep from './EnterPriceStep';

export type { AddGroceryResult } from './types';

interface AddGroceryWhileShoppingModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (result: AddGroceryResult) => void;
  shopId: number;
  shopName: string;
  shoppingListId: number;
  existingGroceries: ShoppingListGrocery[];
  prices: Map<number, Price>;
}

const steps = ['Find Grocery', 'Add to List', 'Enter Price'];

const AddGroceryWhileShoppingModal: React.FC<AddGroceryWhileShoppingModalProps> = ({
  open,
  onClose,
  onSuccess,
  shopId,
  shopName,
  shoppingListId,
  existingGroceries,
  prices,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const apiUrl = process.env.REACT_APP_API_URL;

  // Wizard step
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: Find grocery
  const [findTabValue, setFindTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GrocerySearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [scannerResetTrigger, setScannerResetTrigger] = useState(0);
  const [formData, setFormData] = useState<GroceryFormData>(emptyGroceryFormData);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof GroceryFormData, string>>>({});
  const [selectedCatalog, setSelectedCatalog] = useState<GroceryCatalogItem | null>(null);
  const [selectedImageType, setSelectedImageType] = useState<'front' | 'ingredients' | 'nutrition'>('front');

  // Custom image upload
  const [uploadingCustom, setUploadingCustom] = useState(false);
  const [customImagePreview, setCustomImagePreview] = useState<string | null>(null);
  const [useCustomImage, setUseCustomImage] = useState(false);

  // Select tab: user's groceries not in shopping list
  const [availableGroceries, setAvailableGroceries] = useState<Grocery[]>([]);
  const [availableGroceriesLoading, setAvailableGroceriesLoading] = useState(false);

  // Found/created grocery
  const [grocery, setGrocery] = useState<Grocery | null>(null);
  const [wasNewGrocery, setWasNewGrocery] = useState(false);
  const [wasAddedToList, setWasAddedToList] = useState(false);

  // Step 2: Add to list
  const [quantity, setQuantity] = useState(1);

  // Step 3: Price entry - default to manual tab
  const [priceTabValue, setPriceTabValue] = useState(1);
  const [price, setPrice] = useState('');
  const [isDiscounted, setIsDiscounted] = useState(false);
  const [priceBeforeDiscount, setPriceBeforeDiscount] = useState('');
  const [scannedPrice, setScannedPrice] = useState<string | null>(null);
  const [priceScannerActive, setPriceScannerActive] = useState(false);
  const [priceScannerResetTrigger, setPriceScannerResetTrigger] = useState(0);

  // General state
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setFindTabValue(isMobile ? 0 : 1);
      setSearchQuery('');
      setSearchResults([]);
      setBarcodeInput('');
      setFormData(emptyGroceryFormData);
      setFormErrors({});
      setSelectedCatalog(null);
      setSelectedImageType('front');
      setCustomImagePreview(null);
      setUseCustomImage(false);
      setGrocery(null);
      setWasNewGrocery(false);
      setWasAddedToList(false);
      setQuantity(1);
      setPriceTabValue(1);
      setPrice('');
      setIsDiscounted(false);
      setPriceBeforeDiscount('');
      setScannedPrice(null);
      setError(null);
      setScannerResetTrigger(0);
      setPriceScannerResetTrigger(0);
      setAvailableGroceries([]);

      // Fetch user's groceries not in shopping list
      const fetchAvailableGroceries = async () => {
        setAvailableGroceriesLoading(true);
        try {
          const allGroceries = await apiFetch(`${apiUrl}/api/groceries/`);
          const groceryIdsInList = new Set(existingGroceries.map((item) => item.grocery));
          const available = allGroceries.filter(
            (g: Grocery) => !groceryIdsInList.has(g.id)
          );
          setAvailableGroceries(available);
        } catch (err: any) {
          console.error('Failed to fetch available groceries:', err);
        } finally {
          setAvailableGroceriesLoading(false);
        }
      };
      fetchAvailableGroceries();
    }
  }, [open, isMobile, apiUrl, existingGroceries]);

  // Activate price scanner when on step 3 and scan tab
  useEffect(() => {
    if (open && activeStep === 2 && priceTabValue === 0) {
      const timeout = setTimeout(() => setPriceScannerActive(true), 200);
      return () => clearTimeout(timeout);
    } else {
      setPriceScannerActive(false);
    }
  }, [open, activeStep, priceTabValue]);

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>
        Add New Grocery
      </Typography>

      <StepWizard
        steps={steps}
        activeStep={activeStep}
        sx={{ mb: 3 }}
        alternativeLabel={isMobile}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* STEP 1: Find Grocery */}
      {activeStep === 0 && (
        <FindGroceryStep
          findTabValue={findTabValue}
          setFindTabValue={setFindTabValue}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          setSearchResults={setSearchResults}
          searchLoading={searchLoading}
          setSearchLoading={setSearchLoading}
          barcodeInput={barcodeInput}
          setBarcodeInput={setBarcodeInput}
          barcodeLoading={barcodeLoading}
          setBarcodeLoading={setBarcodeLoading}
          scannerResetTrigger={scannerResetTrigger}
          setScannerResetTrigger={setScannerResetTrigger}
          formData={formData}
          setFormData={setFormData}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          selectedCatalog={selectedCatalog}
          setSelectedCatalog={setSelectedCatalog}
          selectedImageType={selectedImageType}
          setSelectedImageType={setSelectedImageType}
          uploadingCustom={uploadingCustom}
          setUploadingCustom={setUploadingCustom}
          customImagePreview={customImagePreview}
          setCustomImagePreview={setCustomImagePreview}
          useCustomImage={useCustomImage}
          setUseCustomImage={setUseCustomImage}
          availableGroceries={availableGroceries}
          availableGroceriesLoading={availableGroceriesLoading}
          grocery={grocery}
          setGrocery={setGrocery}
          error={error}
          setError={setError}
          loading={loading}
          setLoading={setLoading}
          onClose={onClose}
          isMobile={isMobile}
          apiUrl={apiUrl || ''}
          setWasNewGrocery={setWasNewGrocery}
          setActiveStep={setActiveStep}
          existingGroceries={existingGroceries}
          prices={prices}
          setPrice={setPrice}
          setIsDiscounted={setIsDiscounted}
          setPriceBeforeDiscount={setPriceBeforeDiscount}
          setWasAddedToList={setWasAddedToList}
          open={open}
        />
      )}

      {/* STEP 2: Add to Shopping List */}
      {activeStep === 1 && grocery && (
        <AddToListStep
          grocery={grocery}
          setGrocery={setGrocery}
          error={error}
          setError={setError}
          loading={loading}
          setLoading={setLoading}
          onClose={onClose}
          isMobile={isMobile}
          apiUrl={apiUrl || ''}
          quantity={quantity}
          setQuantity={setQuantity}
          shoppingListId={shoppingListId}
          setActiveStep={setActiveStep}
          setWasAddedToList={setWasAddedToList}
          setSelectedCatalog={setSelectedCatalog}
          setFormData={setFormData}
          prices={prices}
          setPrice={setPrice}
          setIsDiscounted={setIsDiscounted}
          setPriceBeforeDiscount={setPriceBeforeDiscount}
        />
      )}

      {/* STEP 3: Enter Price */}
      {activeStep === 2 && grocery && (
        <EnterPriceStep
          grocery={grocery}
          setGrocery={setGrocery}
          error={error}
          setError={setError}
          loading={loading}
          setLoading={setLoading}
          onClose={onClose}
          isMobile={isMobile}
          apiUrl={apiUrl || ''}
          shopId={shopId}
          shopName={shopName}
          priceTabValue={priceTabValue}
          setPriceTabValue={setPriceTabValue}
          price={price}
          setPrice={setPrice}
          isDiscounted={isDiscounted}
          setIsDiscounted={setIsDiscounted}
          priceBeforeDiscount={priceBeforeDiscount}
          setPriceBeforeDiscount={setPriceBeforeDiscount}
          scannedPrice={scannedPrice}
          setScannedPrice={setScannedPrice}
          priceScannerActive={priceScannerActive}
          priceScannerResetTrigger={priceScannerResetTrigger}
          setPriceScannerResetTrigger={setPriceScannerResetTrigger}
          wasNewGrocery={wasNewGrocery}
          wasAddedToList={wasAddedToList}
          existingGroceries={existingGroceries}
          prices={prices}
          setActiveStep={setActiveStep}
          setSelectedCatalog={setSelectedCatalog}
          setFormData={setFormData}
          onSuccess={onSuccess}
        />
      )}
    </ModalWrapper>
  );
};

export default AddGroceryWhileShoppingModal;
